import { useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck, Loader2, Eye, Pencil, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import DataTable from "@/components/DataTable";

const empty = { gstin: "", party_name: "", legal_name: "", trade_name: "", state: "", registration_date: "", taxpayer_type: "", address: "", city: "", pin: "", contact_person: "", phone: "", email: "", bank_account_no: "", ifsc: "", bank_name: "" };

export default function Parties() {
  const { user, activeBranchId, hasPerm } = useAuth();
  const navigate = useNavigate();
  const isSuper = user.role === "super_admin";
  const canCreate = isSuper || hasPerm("party_create");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  const verifyGst = async () => {
    if (!form.gstin) return;
    setVerifying(true);
    try {
      const { data } = await api.get(`/gst/verify/${encodeURIComponent(form.gstin.toUpperCase())}`);
      if (!data.valid) { toast.error(data.error || "Invalid GST"); return; }
      setForm((f) => ({
        ...f, gstin: data.gstin || f.gstin.toUpperCase(),
        legal_name: data.legal_name || f.legal_name,
        trade_name: data.trade_name || f.trade_name,
        party_name: f.party_name || data.trade_name || data.legal_name,
        state: data.state || f.state,
        registration_date: data.registration_date || f.registration_date,
        taxpayer_type: data.taxpayer_type || f.taxpayer_type,
        address: f.address || data.address || "",
      }));
      setVerified(true);
      toast.success(data.verified ? "GST verified live" : "GST format valid (live API not configured)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/parties", { ...form, branch_id: activeBranchId }); toast.success("Created"); setOpen(false); setForm(empty); setVerified(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!isSuper) { toast.error("Only Super Admin can delete parties"); return; }
    if (!window.confirm("Delete this party? Any associated purchase bills will remain.")) return;
    try { await api.delete(`/parties/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openView = async (party) => {
    setViewing(party); setViewData(null);
    try {
      const { data } = await api.get(`/parties/${party.id}`);
      setViewData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setViewing(null); }
  };

  const openEdit = (p) => { setEditing(p); setEditForm({ ...empty, ...p }); };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const patch = {
        trade_name: editForm.trade_name, address: editForm.address,
        city: editForm.city, pin: editForm.pin,
        contact_person: editForm.contact_person, phone: editForm.phone,
        email: editForm.email, bank_account_no: editForm.bank_account_no,
        ifsc: editForm.ifsc, bank_name: editForm.bank_name,
      };
      await api.put(`/parties/${editing.id}`, patch);
      toast.success("Party updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredColumns = [
    { key: "party_name", label: "Party", sortable: true, render: (p) => <span className="font-medium">{p.party_name || p.trade_name}</span> },
    { key: "gstin", label: "GSTIN", render: (p) => <span className="text-slate-600 font-mono text-xs">{p.gstin || "—"}</span> },
    { key: "state", label: "State", render: (p) => <span className="text-slate-600">{p.state || "—"}</span> },
    { key: "phone", label: "Phone", render: (p) => <span className="text-slate-600">{p.phone || "—"}</span> },
    { key: "gst_verified", label: "Verified", render: (p) => p.gst_verified ? <Badge className="rounded-none bg-green-600">Verified</Badge> : <Badge variant="outline" className="rounded-none">Format only</Badge> },
    { key: "imported_from_tally", label: "Source", render: (p) => p.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
  ];

  const tableFilters = [
    { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
  ];

  return (
    <div className="space-y-6" data-testid="parties-page">
      <div className="flex justify-end">
        {canCreate && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setVerified(false); } }}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-party-btn"><Plus className="w-4 h-4 mr-2" />Add Party</Button></DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>New Party / Supplier</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 p-3">
                  <Label>GSTIN (15-character Indian GST) — optional</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={form.gstin} onChange={(e) => { setForm({ ...form, gstin: e.target.value.toUpperCase() }); setVerified(false); }} className="rounded-none font-mono uppercase" placeholder="29ABCDE1234F1Z5 (leave blank if unregistered)" maxLength={15} data-testid="party-gstin" />
                    <Button type="button" onClick={verifyGst} disabled={verifying || form.gstin.length !== 15} variant="outline" className="rounded-none" data-testid="party-verify-gst">
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" />Verify</>}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Party Name {!form.gstin && <span className="text-red-600">*</span>}</Label><Input required={!form.gstin} value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} className="rounded-none mt-1" data-testid="party-name" /></div>
                  <div><Label>Trade Name</Label><Input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} className="rounded-none mt-1" /></div>
                  <div className="col-span-2"><Label>Legal Business Name {verified && <Badge className="ml-2 rounded-none bg-green-600">Locked</Badge>}</Label><Input value={form.legal_name} readOnly={verified} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className={`rounded-none mt-1 ${verified ? "bg-slate-50" : ""}`} /></div>
                  <div><Label>State {verified && <Badge className="ml-2 rounded-none bg-green-600">Locked</Badge>}</Label><Input value={form.state} readOnly={verified} onChange={(e) => setForm({ ...form, state: e.target.value })} className={`rounded-none mt-1 ${verified ? "bg-slate-50" : ""}`} /></div>
                  <div><Label>Taxpayer Type</Label><Input value={form.taxpayer_type} readOnly={verified} onChange={(e) => setForm({ ...form, taxpayer_type: e.target.value })} className={`rounded-none mt-1 ${verified ? "bg-slate-50" : ""}`} /></div>
                  <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none mt-1" /></div>
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none mt-1" /></div>
                  <div><Label>PIN</Label><Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="rounded-none mt-1" /></div>
                  <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-none mt-1" /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" /></div>
                  <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" /></div>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Bank Details (optional)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="rounded-none mt-1" /></div>
                    <div><Label>Account No.</Label><Input value={form.bank_account_no} onChange={(e) => setForm({ ...form, bank_account_no: e.target.value })} className="rounded-none mt-1 font-mono" /></div>
                    <div><Label>IFSC</Label><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className="rounded-none mt-1 font-mono" /></div>
                  </div>
                </div>
                <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="party-save">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <DataTable
        endpoint="/parties"
        columns={filteredColumns}
        filters={tableFilters}
        searchPlaceholder="Search by name, GSTIN, city, phone..."
        extraQuery={{ branch_id: activeBranchId || undefined }}
        reloadKey={reloadKey}
        actions={(p) => (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-blue-700" onClick={() => navigate(`/parties/${p.id}/ledger`)} data-testid={`ledger-party-${p.id}`} title="View Ledger"><BookOpen className="w-4 h-4" /></Button>
            {isSuper && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => openView(p)} data-testid={`view-party-${p.id}`}><Eye className="w-4 h-4" /></Button>}
            {isSuper && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => openEdit(p)} data-testid={`edit-party-${p.id}`}><Pencil className="w-4 h-4" /></Button>}
            {isSuper && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-red-600" onClick={() => remove(p.id)} data-testid={`delete-party-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
          </div>
        )}
        dataTestid="parties-table"
      />

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) { setViewing(null); setViewData(null); } }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Party Detail — {viewing?.party_name || viewing?.trade_name}</DialogTitle></DialogHeader>
          {!viewData && <div className="text-sm text-slate-500">Loading…</div>}
          {viewData && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">GSTIN</Label><div className="font-mono">{viewData.party.gstin}</div></div>
                <div><Label className="text-xs">State</Label><div>{viewData.party.state || "-"}</div></div>
                <div><Label className="text-xs">Legal Name</Label><div>{viewData.party.legal_name || "-"}</div></div>
                <div><Label className="text-xs">Trade Name</Label><div>{viewData.party.trade_name || "-"}</div></div>
                <div><Label className="text-xs">Phone</Label><div>{viewData.party.phone || "-"}</div></div>
                <div><Label className="text-xs">Email</Label><div>{viewData.party.email || "-"}</div></div>
                <div className="col-span-2"><Label className="text-xs">Address</Label><div>{viewData.party.address || "-"}, {viewData.party.city || ""} {viewData.party.pin || ""}</div></div>
                <div><Label className="text-xs">Bank</Label><div>{viewData.party.bank_name || "-"}</div></div>
                <div><Label className="text-xs">A/c</Label><div className="font-mono">{viewData.party.bank_account_no || "-"}</div></div>
                <div><Label className="text-xs">IFSC</Label><div className="font-mono">{viewData.party.ifsc || "-"}</div></div>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Purchase Bills</div>
                  <div className="text-sm">Total: <b>{formatINR(viewData.total_purchase)}</b> ({viewData.bill_count} bills)</div>
                </div>
                <div className="max-h-48 overflow-auto border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr><th className="p-2 text-left">Bill #</th><th className="p-2 text-left">Date</th><th className="p-2 text-right">Total</th></tr></thead>
                    <tbody>
                      {viewData.bills.length === 0 && <tr><td colSpan={3} className="p-3 text-center text-slate-400">No bills yet</td></tr>}
                      {viewData.bills.map((b) => (
                        <tr key={b.id} className="border-t"><td className="p-2 font-mono">{b.bill_number}</td><td className="p-2">{b.bill_date}</td><td className="p-2 text-right">{formatINR(b.grand_total)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog — Super Admin only */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit Party</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><Label className="text-xs">GSTIN (read-only)</Label><Input readOnly value={editForm.gstin} className="rounded-none mt-1 bg-slate-50 font-mono" /></div>
              <div><Label className="text-xs">Legal Name (read-only)</Label><Input readOnly value={editForm.legal_name} className="rounded-none mt-1 bg-slate-50" /></div>
              <div><Label className="text-xs">State (read-only)</Label><Input readOnly value={editForm.state} className="rounded-none mt-1 bg-slate-50" /></div>
              <div><Label className="text-xs">Taxpayer Type (read-only)</Label><Input readOnly value={editForm.taxpayer_type || ""} className="rounded-none mt-1 bg-slate-50" /></div>
              <div className="col-span-2"><Label>Trade Name</Label><Input value={editForm.trade_name || ""} onChange={(e) => setEditForm({ ...editForm, trade_name: e.target.value })} className="rounded-none mt-1" data-testid="edit-party-trade" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="rounded-none mt-1" data-testid="edit-party-address" /></div>
              <div><Label>City</Label><Input value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>PIN</Label><Input value={editForm.pin || ""} onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Contact Person</Label><Input value={editForm.contact_person || ""} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Phone</Label><Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-none mt-1" /></div>
              <div className="col-span-2"><Label>Email</Label><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Bank Name</Label><Input value={editForm.bank_name || ""} onChange={(e) => setEditForm({ ...editForm, bank_name: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Account No</Label><Input value={editForm.bank_account_no || ""} onChange={(e) => setEditForm({ ...editForm, bank_account_no: e.target.value })} className="rounded-none mt-1 font-mono" /></div>
              <div><Label>IFSC</Label><Input value={editForm.ifsc || ""} onChange={(e) => setEditForm({ ...editForm, ifsc: e.target.value.toUpperCase() })} className="rounded-none mt-1 font-mono" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="edit-party-save">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
